/**
 * hooks/useApi.js
 * Generic data-fetching hook + specialised hooks for each resource.
 * Handles loading, error, and refetch patterns consistently.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import * as api from "../api/client";

// ─── Generic fetch hook ───────────────────────────────────────────────────────
export function useFetch(fetcher, deps = []) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const mountedRef             = useRef(true);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const result = await fetcher();
      if (mountedRef.current) setData(result);
    } catch (err) {
      if (mountedRef.current) setError(err.message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => { mountedRef.current = false; };
  }, [load]);

  return { data, loading, error, refetch: load };
}

// ─── Mutation hook (POST / PATCH / DELETE) ────────────────────────────────────
export function useMutation(mutator) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const execute = useCallback(async (...args) => {
    setLoading(true); setError(null);
    try {
      const result = await mutator(...args);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [mutator]);

  return { execute, loading, error };
}

// ─── Client hooks ─────────────────────────────────────────────────────────────

export const useClients = (userId) => useFetch(() => userId ? api.clients.list() : Promise.resolve(null),[userId]);
export const useClient      = (id)  => useFetch(() => api.clients.get(id), [id]);

// ─── RFP hooks ────────────────────────────────────────────────────────────────
export const useRFP         = (cid) => useFetch(() => api.rfp.list(cid), [cid]);

// ─── Catalogue hook ───────────────────────────────────────────────────────────
export const useCatalogue   = (svc) => useFetch(() => api.catalogue.list(svc), [svc]);

// ─── Available months ─────────────────────────────────────────────────────────
export const useMonths      = (cid) => useFetch(
  () => cid ? api.analysis.availableMonths(cid).then(r => r.months) : Promise.resolve([]),
  [cid]
);

// ─── Monthly analysis ─────────────────────────────────────────────────────────
export const useMonthAnalysis = (cid, month) => useFetch(
  () => (cid && month) ? api.analysis.month(cid, month) : Promise.resolve(null),
  [cid, month]
);

// ─── Range analysis ───────────────────────────────────────────────────────────
export const useRangeAnalysis = (cid, months) => useFetch(
  () => (cid && months?.length) ? api.analysis.range(cid, months) : Promise.resolve(null),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [cid, JSON.stringify(months)]
);
